import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    downloadFileByUrl,
    downloadSearchExport,
    downloadSearchRepairTemplate,
    importSearchRepairResults,
    loadMetadata,
    loadModules,
} from './api';
import {
    clearStoredSearchState,
    loadStoredSearchState,
    saveStoredSearchState,
} from './searchState';
import SearchableSelect from './SearchableSelect';


const fallbackMetadata = {
    choices: {
        repair_statuses: [
            { value: 'Ожидает ремонт', label: 'Ожидает ремонт' },
            { value: 'Сборка', label: 'Сборка' },
            { value: 'Готов', label: 'Готов' },
            { value: 'Готов к отгрузке', label: 'Готов к отгрузке' },
        ],
        movement_statuses: [
            { value: 'В РЦ', label: 'В РЦ' },
            { value: 'В заявке', label: 'В заявке' },
        ],
        systems: [
            { value: '5X', label: '5X' },
            { value: '2X', label: '2X' },
            { value: 'TE 2X', label: 'TE 2X' },
            { value: 'CIT 2X', label: 'CIT 2X' },
            { value: 'CIT 5X', label: 'CIT 5X' },
        ],
    },
    locations: [
        { id: 1, city: 'Тюмень', address: 'Локальный пример' },
        { id: 2, city: 'Москва', address: 'Локальный пример' },
    ],
};

const initialFilters = {
    q: '',
    serial_numbers: '',
    repair_status: '',
    movement_status: '',
    system: '',
    location: '',
    engineer: '',
    request_slug: '',
};

const emptyResultState = {
    items: [],
    pagination: null,
    searchStats: null,
    loading: false,
    warning: '',
};


function extractErrorMessage(error, fallback) {
    return error?.response?.data?.detail || fallback;
}


function formatDate(value) {
    if (!value) {
        return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString('ru-RU');
}


function hasActiveFilters(filters) {
    return Object.values(filters).some((value) => String(value || '').trim() !== '');
}


function buildSearchParams(filters) {
    const serialNumbers = String(filters.serial_numbers || '').trim();
    return {
        page: 1,
        page_size: serialNumbers ? 5000 : 250,
        ...filters,
    };
}


export default function SearchPage() {
    const navigate = useNavigate();
    const location = useLocation();

    const [filters, setFilters] = useState(initialFilters);
    const [metadata, setMetadata] = useState(fallbackMetadata);
    const [resultState, setResultState] = useState(emptyResultState);
    const [hasSearched, setHasSearched] = useState(false);
    const [importState, setImportState] = useState({
        loading: false,
        templateLoading: false,
        file: null,
        message: '',
        error: '',
        updatedCount: 0,
        invalidItems: [],
    });
    const [exportLoading, setExportLoading] = useState(false);
    const [downloadState, setDownloadState] = useState({
        excelId: null,
        pdfId: null,
    });

    useEffect(() => {
        let cancelled = false;

        async function bootstrap() {
            try {
                const metadataPayload = await loadMetadata();
                if (cancelled) {
                    return;
                }
                setMetadata(metadataPayload);
            } catch (_error) {
                if (!cancelled) {
                    setMetadata(fallbackMetadata);
                }
            }

            const shouldRestoreSearch = Boolean(location.state?.restoreSearch);
            const stored = loadStoredSearchState();
            if (!shouldRestoreSearch || !stored || cancelled) {
                return;
            }

            setFilters(stored.filters || initialFilters);
            setResultState(stored.resultState || emptyResultState);
            setHasSearched(Boolean(stored.hasSearched));

            if (stored.scrollY != null && location.state?.restoreSearch) {
                window.setTimeout(() => {
                    window.scrollTo(0, stored.scrollY);
                }, 0);
            }
        }

        bootstrap();

        return () => {
            cancelled = true;
        };
    }, [location.state]);

    const locationOptions = useMemo(
        () => (metadata.locations || [])
            .map((item) => item.city)
            .filter(Boolean)
            .map((city) => ({ value: city, label: city })),
        [metadata.locations],
    );

    const visibleCount = resultState.items.length;
    const totalCount = resultState.pagination?.total_items ?? 0;
    const missingCount = resultState.searchStats?.missing_count ?? 0;
    const missingSerialsPreview = resultState.searchStats?.missing_serial_numbers?.slice(0, 8) || [];

    function persistState(nextFilters, nextResultState, nextHasSearched, scrollY = 0) {
        saveStoredSearchState({
            filters: nextFilters,
            resultState: nextResultState,
            hasSearched: nextHasSearched,
            scrollY,
        });
    }

    function updateFilter(name, value) {
        setFilters((current) => ({
            ...current,
            [name]: value,
        }));
    }

    async function runSearch(nextFilters) {
        if (!hasActiveFilters(nextFilters)) {
            const clearedState = {
                ...emptyResultState,
                warning: 'Укажи фильтр или вставь список серийных номеров, чтобы запустить поиск.',
            };
            setResultState(clearedState);
            setHasSearched(false);
            clearStoredSearchState();
            return;
        }

        setResultState((current) => ({
            ...current,
            loading: true,
            warning: '',
        }));

        try {
            const payload = await loadModules(buildSearchParams(nextFilters));
            const nextResultState = {
                items: payload.items || [],
                pagination: payload.pagination || null,
                searchStats: payload.search_stats || null,
                loading: false,
                warning: '',
            };
            setResultState(nextResultState);
            setHasSearched(true);
            persistState(nextFilters, nextResultState, true);
        } catch (error) {
            const nextResultState = {
                ...emptyResultState,
                warning: extractErrorMessage(error, 'Не удалось выполнить поиск.'),
            };
            setResultState(nextResultState);
            setHasSearched(false);
        }
    }

    async function submitFilters(event) {
        event.preventDefault();
        await runSearch(filters);
    }

    function resetFilters() {
        setFilters(initialFilters);
        setResultState(emptyResultState);
        setHasSearched(false);
        setImportState((current) => ({
            ...current,
            message: '',
            error: '',
            invalidItems: [],
            updatedCount: 0,
        }));
        clearStoredSearchState();
    }

    async function handleExport() {
        if (!hasSearched) {
            setResultState((current) => ({
                ...current,
                warning: 'Сначала выполни поиск, потом выгружай Excel.',
            }));
            return;
        }

        setExportLoading(true);
        try {
            await downloadSearchExport(buildSearchParams(filters));
        } catch (error) {
            setResultState((current) => ({
                ...current,
                warning: extractErrorMessage(error, 'Не удалось выгрузить Excel.'),
            }));
        } finally {
            setExportLoading(false);
        }
    }

    async function handleDownloadTemplate() {
        setImportState((current) => ({
            ...current,
            templateLoading: true,
            error: '',
        }));
        try {
            await downloadSearchRepairTemplate();
        } catch (error) {
            setImportState((current) => ({
                ...current,
                error: extractErrorMessage(error, 'Не удалось скачать шаблон импорта.'),
            }));
        } finally {
            setImportState((current) => ({
                ...current,
                templateLoading: false,
            }));
        }
    }

    async function handleImportRepairResults(event) {
        event.preventDefault();
        if (!importState.file) {
            setImportState((current) => ({
                ...current,
                error: 'Сначала выбери Excel файл.',
            }));
            return;
        }

        setImportState((current) => ({
            ...current,
            loading: true,
            message: '',
            error: '',
            invalidItems: [],
        }));

        const formData = new FormData();
        formData.append('file', importState.file);

        try {
            const payload = await importSearchRepairResults(formData);
            setImportState({
                loading: false,
                templateLoading: false,
                file: null,
                message: `Импорт завершен. Обновлено модулей: ${payload.updated_count}.`,
                error: '',
                updatedCount: payload.updated_count,
                invalidItems: payload.invalid_items || [],
            });

            if (hasSearched) {
                await runSearch(filters);
            }
        } catch (error) {
            setImportState((current) => ({
                ...current,
                loading: false,
                message: '',
                error: extractErrorMessage(error, 'Не удалось импортировать Excel.'),
            }));
        }
    }

    async function handleDownloadFile(item, kind) {
        const fileUrl = kind === 'excel' ? item.request_send?.excel_file_url : item.request_send?.pdf_file_url;
        if (!fileUrl) {
            return;
        }

        setDownloadState((current) => ({
            ...current,
            [`${kind}Id`]: item.id,
        }));

        try {
            await downloadFileByUrl(fileUrl, `${item.serial_number}-${kind === 'excel' ? 'act' : 'ttn'}`);
        } catch (error) {
            setResultState((current) => ({
                ...current,
                warning: extractErrorMessage(error, 'Не удалось скачать файл.'),
            }));
        } finally {
            setDownloadState((current) => ({
                ...current,
                [`${kind}Id`]: null,
            }));
        }
    }

    function handleOpenDetails(item) {
        persistState(filters, resultState, hasSearched, window.scrollY);
        navigate(`/app/search/${item.id}`);
    }

    const summaryCards = [
        { label: 'Найдено', value: hasSearched ? visibleCount : 0 },
        { label: 'Всего в выдаче', value: hasSearched ? totalCount : 0 },
        { label: 'Не найдено', value: hasSearched ? missingCount : 0 },
    ];

    return (
        <div className="shell-page">
            <section className="shell-section shell-section--pageTop">
                <div className="shell-pageHeaderCompact">
                    <div className="shell-pageBandIntro">
                        <h3>Поиск серийных номеров</h3>
                    </div>

                    <div className="shell-pageSummary shell-pageSummary--queueTop shell-pageSummary--header">
                        {summaryCards.map((item) => (
                            <div key={item.label} className="shell-pageSummaryCard shell-pageSummaryCard--compact">
                                <span>{item.label}</span>
                                <strong>{item.value}</strong>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {resultState.warning ? <div className="shell-banner">{resultState.warning}</div> : null}
            {importState.error ? <div className="shell-banner">{importState.error}</div> : null}
            {importState.message ? <div className="shell-inlineMessage">{importState.message}</div> : null}
            {missingCount ? (
                <div className="shell-banner shell-banner--warning">
                    Не найдены серийные номера: {missingSerialsPreview.join(', ')}
                    {resultState.searchStats?.missing_serial_numbers?.length > missingSerialsPreview.length ? ' ...' : ''}
                </div>
            ) : null}

            <section className="shell-grid shell-grid--main">
                <div className="shell-section">
                    <div className="shell-sectionHeader">
                        <div>
                            <h3>Фильтры и пакетный поиск</h3>
                        </div>
                    </div>

                    <form className="shell-searchForm" onSubmit={submitFilters}>
                        <label className="shell-field">
                            <span>Общий поиск</span>
                            <input
                                value={filters.q}
                                onChange={(event) => updateFilter('q', event.target.value)}
                                placeholder="Серийный номер, партия, инженер, комментарий"
                            />
                        </label>

                        <label className="shell-field">
                            <span>Статус ремонта</span>
                            <SearchableSelect
                                value={filters.repair_status}
                                options={metadata.choices?.repair_statuses || []}
                                onChange={(value) => updateFilter('repair_status', value)}
                                placeholder="Все"
                            />
                        </label>

                        <label className="shell-field">
                            <span>Статус движения</span>
                            <SearchableSelect
                                value={filters.movement_status}
                                options={metadata.choices?.movement_statuses || []}
                                onChange={(value) => updateFilter('movement_status', value)}
                                placeholder="Все"
                            />
                        </label>

                        <label className="shell-field">
                            <span>Система</span>
                            <SearchableSelect
                                value={filters.system}
                                options={metadata.choices?.systems || []}
                                onChange={(value) => updateFilter('system', value)}
                                placeholder="Все"
                            />
                        </label>

                        <label className="shell-field">
                            <span>Локация</span>
                            <SearchableSelect
                                value={filters.location}
                                options={locationOptions}
                                onChange={(value) => updateFilter('location', value)}
                                placeholder="Все"
                            />
                        </label>

                        <label className="shell-field">
                            <span>Инженер</span>
                            <input
                                value={filters.engineer}
                                onChange={(event) => updateFilter('engineer', event.target.value)}
                                placeholder="Фильтр по инженеру"
                            />
                        </label>

                        <label className="shell-field">
                            <span>Slug заявки</span>
                            <input
                                value={filters.request_slug}
                                onChange={(event) => updateFilter('request_slug', event.target.value)}
                                placeholder="Например tjumen-demo-44"
                            />
                        </label>

                        <label className="shell-field shell-field--full">
                            <span>Список серийных номеров</span>
                            <textarea
                                value={filters.serial_numbers}
                                onChange={(event) => updateFilter('serial_numbers', event.target.value)}
                                placeholder="Вставь серийники через Enter, запятую или ;"
                            />
                        </label>

                        <div className="shell-searchActions">
                            <button type="submit" className="shell-actionButton shell-actionButton--primary">Применить</button>
                            <button type="button" className="shell-actionButton shell-actionButton--secondary" onClick={resetFilters}>Сбросить</button>
                            <button type="button" className="shell-actionButton shell-actionButton--secondary" onClick={handleExport} disabled={exportLoading}>
                                {exportLoading ? 'Выгружаем...' : 'Экспорт в Excel'}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="shell-section">
                    <div className="shell-sectionHeader">
                        <div>
                            <h3>Импорт закрытий</h3>
                            <p className="shell-pageSubtitle shell-pageSubtitle--compact">
                                Сюда загружается Excel с результатами ремонта от инженеров. Если файл еще не подготовлен в нужном формате, сначала скачай шаблон и заполни его по колонкам.
                            </p>
                        </div>
                    </div>

                    <form className="shell-searchForm shell-searchForm--embedded" onSubmit={handleImportRepairResults}>
                        <label className="shell-field shell-field--full">
                            <span>Excel файл</span>
                            <input
                                type="file"
                                accept=".xlsx,.xlsm,.xltx,.xltm,.xls"
                                onChange={(event) => setImportState((current) => ({
                                    ...current,
                                    file: event.target.files?.[0] || null,
                                    error: '',
                                    message: '',
                                }))}
                            />
                        </label>

                        <div className="shell-searchActions">
                            <button type="submit" className="shell-actionButton shell-actionButton--primary" disabled={importState.loading}>
                                {importState.loading ? 'Импортируем...' : 'Загрузить Excel'}
                            </button>
                            <button type="button" className="shell-actionButton shell-actionButton--secondary" onClick={handleDownloadTemplate} disabled={importState.templateLoading}>
                                {importState.templateLoading ? 'Готовим шаблон...' : 'Скачать шаблон'}
                            </button>
                        </div>
                    </form>

                    {importState.invalidItems.length ? (
                        <div className="shell-miniModuleSection">
                            <div className="shell-sectionHeader shell-sectionHeader--nested">
                                <div>
                                    <h3>Ошибки импорта</h3>
                                </div>
                                <span>{importState.invalidItems.length} шт.</span>
                            </div>

                            <div className="shell-tableWrap">
                                <table className="shell-table">
                                    <thead>
                                        <tr>
                                            <th>Серийный номер</th>
                                            <th>Локация</th>
                                            <th>Дата поступления</th>
                                            <th>Причина</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {importState.invalidItems.slice(0, 20).map((item, index) => (
                                            <tr key={`${item.serial_number}-${index}`} className="shell-tableRow">
                                                <td>{item.serial_number || '—'}</td>
                                                <td>{item.location || '—'}</td>
                                                <td>{item.arrival_date ? formatDate(item.arrival_date) : '—'}</td>
                                                <td>{item.note || 'Ошибка импорта'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : null}
                </div>
            </section>

            <section className="shell-section">
                <div className="shell-sectionHeader">
                    <div>
                        <h3>Результаты поиска</h3>
                    </div>
                    <span>{hasSearched ? `${visibleCount} из ${totalCount}` : 'Поиск еще не запускался'}</span>
                </div>

                {!hasSearched ? (
                    <div className="shell-emptyState shell-emptyState--compact">
                        Вставь серийные номера или задай фильтры, потом нажми `Применить`.
                    </div>
                ) : resultState.loading ? (
                    <div className="shell-emptyState shell-emptyState--compact">Загружаем результаты поиска...</div>
                ) : resultState.items.length ? (
                    <div className="shell-tableWrap">
                        <table className="shell-table">
                            <thead>
                                <tr>
                                    <th>Серийный номер</th>
                                    <th>Партия / система</th>
                                    <th>Статусы</th>
                                    <th>Локация</th>
                                    <th>Инженер</th>
                                    <th>Заявка</th>
                                    <th>Акт</th>
                                    <th>ТТН</th>
                                    <th>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {resultState.items.map((item) => (
                                    <tr key={item.id} className="shell-tableRow">
                                        <td>
                                            <strong>{item.serial_number}</strong>
                                            <div className="shell-tableHint">{formatDate(item.arrival_date)}</div>
                                        </td>
                                        <td>
                                            {item.party_number || '—'}
                                            <div className="shell-tableHint">{item.system || '—'}</div>
                                        </td>
                                        <td>
                                            <div className="shell-pillRow">
                                                <span className="shell-pillTag">{item.repair_status || '—'}</span>
                                                <span className="shell-pillTag">{item.movement_status || '—'}</span>
                                            </div>
                                        </td>
                                        <td>{item.location?.city || '—'}</td>
                                        <td>{item.engineer || '—'}</td>
                                        <td>{item.request_send?.application_name || item.request_send?.slug || '—'}</td>
                                        <td>
                                            {item.request_send?.excel_file_url ? (
                                                <button
                                                    type="button"
                                                    className="shell-actionButton shell-actionButton--secondary shell-actionButton--small"
                                                    onClick={() => handleDownloadFile(item, 'excel')}
                                                    disabled={downloadState.excelId === item.id}
                                                >
                                                    {downloadState.excelId === item.id ? 'Скачиваем...' : 'Акт'}
                                                </button>
                                            ) : '—'}
                                        </td>
                                        <td>
                                            {item.request_send?.pdf_file_url ? (
                                                <button
                                                    type="button"
                                                    className="shell-actionButton shell-actionButton--secondary shell-actionButton--small"
                                                    onClick={() => handleDownloadFile(item, 'pdf')}
                                                    disabled={downloadState.pdfId === item.id}
                                                >
                                                    {downloadState.pdfId === item.id ? 'Скачиваем...' : 'ТТН'}
                                                </button>
                                            ) : '—'}
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="shell-actionButton shell-actionButton--primary shell-actionButton--small"
                                                onClick={() => handleOpenDetails(item)}
                                            >
                                                Детали
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="shell-emptyState shell-emptyState--compact">По заданным фильтрам ничего не найдено.</div>
                )}
            </section>
        </div>
    );
}
